<template>
  <div :class="[
    'timeline_container',
    props.layout.scrollBarPosition === 'right' ? 'reverse' : '',
  ]">
    <div :class="[
      'timeline_container-dates_list'
    ]">
      <ul class="timeline_container-date_items">
        <li
          v-for="(item, idx) in props.timelineData"
          :key="item.title.text"
          :class="[
            'timeline_container-date_item',
            { selected: item.title.text=== activeItem }
          ]"
          :aria-current="item.title.text === activeItem ? 'date' : undefined"
        >
          <a
            href="#"
            @click.prevent="scrollToItem(item.title.text)"
            :aria-label="`View ${item.title.text}`"
          >
            {{ item.title.text }}
          </a>
        </li>
      </ul>
    </div>

    <div class="timeline_container-content">
      <div class="timeline_container-wrapper" ref="eventsWrapperRef">
        <section
            v-for="(item, idx) in props.timelineData"
            :key="item.title.text"
            :ref="el => sectionRefs[idx] = el"
            :class="[
                'timeline_container-text_content', 
                'year-section',
                getBgColorClass(item.bgColor),
                item.image_alignment === 'right' ? 'reverse-order' : ''
            ]"           
        >
          <div class="timeline_container-image_block">
            <img
              loading="lazy"
              :src="item.image.media"
              :alt="item.image.alt"
              class="timeline_container-image mobile-hidden"
            />
          </div>
          <div class="timeline_container-text_container">
              <h2 v-if="item.title.text"
                :class="[
                  'timeline_container-text_title', 
                  'headline-feature', 
                  getTextColorClass(item.title.color)
                ]"
                :aria-label="item.title.text"
                role="heading"
                aria-level="2"
              >
                {{ item.title.text }}
              </h2>
              <h2 v-if="item.subtitle.text"
                :class="[
                  'timeline_container-text_subtitle', 
                  'headline-m', 
                  getTextColorClass(item.subtitle.color)
                ]"
                :aria-label="item.subtitle.text"
                role="heading"
                aria-level="3"
              >
                {{ item.subtitle.text }}
              </h2>
              <p 
                v-if="richTextHasText(item.description.text)"
                :class="[
                  'timeline_container-text_p', 
                  getTextColorClass(item.description.color)
                ]"
                v-html="useRichTextFormatting(item.description.text)"></p>
              <RothcoCta
                ctaType="link"
                v-bind="getCtaLink(item.cta)"
                :class="['timeline_container-text_link', ...getCtaClasses(item.cta)]"
                role="button"
                :aria-label="item.cta.aria_label"
              >
                {{ item.cta.label }}
              </RothcoCta>
          </div>
        </section>
      </div> 

      <!-- ACTION AREA (static) -->
      <div 
        :class="[
          'timeline_container-action',
          getBgColorClass(props.action.bgColor)
        ]"
      >
        <p :class="[
          'paragraph-details',
          getTextColorClass(props.action.continueText.color)
        ]" 
          :aria-label="props.action.continueText.aria_label" v-if="props.action.continueText.label">
          {{props.action.continueText.label}}
        </p>
        <RothcoCta
          v-if="props.action.skip.label"
          cta-type="button"
          :class="[
              'timeline_container-action_btn',
              'small',
              props.action.skip.style
          ]"
          :aria-label="props.action.skip.aria_label"
          role="button"
          @click="skipTimeline"
        >
          {{ props.action.skip.label }}
        </RothcoCta>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useScrollLock, useIntersectionObserver } from '@vueuse/core'
import RothcoCta from "@/components/RothcoCta.vue";
import { getCtaLink } from "@/helpers/interfaces/Cta";
import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";
import {
        useRichTextFormatting,
        richTextHasText
    } from "@/helpers/composables/builderFieldUtility.ts";

interface TimelineEntry {
  title: { text: string, color: string},
  subtitle: { text: string, color: string},
  description: { text: string, color: string},
  image: { media : string, alt: string}
  cta?: { href: string; label: string },
  image_alignment: string
}

interface Action {
  bgColor: string,
  continueText: { label: string, color: string, aria_label: string },
  skip: { label: string, style: string, aria_label: string }
}

interface Layout {
  scrollBarPosition: string
}

let props = defineProps({
    layout: {
      type: Object as PropType<Layout>,
      required: false,
      default: { scrollBarPosition: ''}
    },
    timelineData: {
        type: Array as PropType<TimelineEntry[]>,
        required: false,
        default: () => [],
    },
    action: {
        type: Object as PropType<Action>,
        required: false,
        default: { bgColor: '', continueText: {label : '', color : '', aria_label: ''},  skip: {label : '', style : 'secondary', aria_label: ''}}
    }
});

const sectionRefs = ref<HTMLElement[]>([])

const activeItem = ref<number>(props.timelineData[0].title.text)

const eventsWrapperRef = ref<HTMLElement | null>(null)

const isScrollLock = useScrollLock(eventsWrapperRef);

const activeObserverStops: (() => void)[] = []

const getBgColorClass = (bgColor) => `bg-${bgColor}`;

const getTextColorClass = (color) => `text-${color}`;

let breakpoints = useRothcoBreakpoints();

const getCtaClasses = (cta) => {
    let classes = [cta.style ? cta.style : "primary"];
    if (cta.size != "default") classes.push(cta.size);
    return classes;
};

onMounted(() => {
  sectionRefs.value.forEach((el, idx) => {
    if (!el) return;

    const { stop } = useIntersectionObserver(
      el,
      ([entry]) => {
        if (entry.isIntersecting) {
          activeItem.value = props.timelineData[idx].title.text
        }
      },
      {
        root: eventsWrapperRef.value,
        threshold: 0.5,
      }
    )

    activeObserverStops.push(stop)
  })

})

const skipTimeline = () => {
  const el = document.querySelector('.timeline_container-action');
  if (!el) return;

  const offset = 100;
  const top = el.getBoundingClientRect().top + window.scrollY + offset;
  window.scrollTo({
    top,
    behavior: 'smooth',
  });
}

function scrollToItem(title: number) {
  const idx = props.timelineData.findIndex((item) => item.title.text === title)
  if (idx === -1 || !eventsWrapperRef.value || !sectionRefs.value[idx]) return

  if (breakpoints.smaller('laptop').value) {
    const item = sectionRefs.value[idx];
    const itemLeft = item.offsetLeft;
    const itemWidth = item.offsetWidth;
    const containerWidth = eventsWrapperRef.value.offsetWidth;

    eventsWrapperRef.value.scrollTo({
      left: itemLeft - (containerWidth - itemWidth) / 2,
      behavior: 'smooth'
    });
  } else {
    const topOfSection = sectionRefs.value[idx].offsetTop
    eventsWrapperRef.value.scrollTo({
      top: topOfSection,
      behavior: 'smooth',
    })
  }
}
</script>
